<template>
  <div class="v2-card task-list" data-qid="engagement-task-list">

    <!-- Search, filters and actions -->
    <div class="task-list__bar">
      <div class="task-list__search">
        <font-awesome-icon icon="search"/>
        <input type="text"
               v-model="keyword"
               :placeholder="$t('tasks.v2.list.search')"
               data-qid="task-list-search"
        />
      </div>

      <span class="task-chip"
            :class="{'task-chip--on': mine}"
            @click="mine = !mine"
            data-qid="task-list-mine"
      >
        <font-awesome-icon icon="user"/>{{ $t('tasks.v2.list.assigned_to_me') }}
        <font-awesome-icon v-if="mine" icon="times" class="task-chip__x"/>
      </span>

      <b-dropdown v-for="filter in filterMenus"
                  :key="'filter-' + filter.key"
                  size="sm"
                  variant="link"
                  no-caret
                  toggle-class="task-chip"
                  :class="{'task-chip-wrap--on': filters[filter.key] !== 'all'}"
                  :data-qid="'task-list-filter-' + filter.key"
      >
        <template #button-content>
          {{ filter.label }}: {{ optionLabel(filter, filters[filter.key]) }}
          <font-awesome-icon icon="chevron-down" class="ml-1"/>
        </template>
        <b-dropdown-item v-for="option in filter.options"
                         :key="filter.key + '-' + option.value"
                         :active="filters[filter.key] === option.value"
                         @click="filters[filter.key] = option.value"
        >
          {{ option.name }}
        </b-dropdown-item>
      </b-dropdown>

      <span v-if="filtersActive"
            class="task-list__reset"
            @click="resetFilters"
            data-qid="task-list-reset">
        {{ $t('system.reset_filters') }}
      </span>

      <div class="task-list__actions">
        <span class="icon-btn"
              v-if="canManageAssignees"
              @click="$emit('manage-assignees')"
              v-b-tooltip.hover
              :title="$t('projects.engagement.assignees.button')"
              data-qid="manage-task-assignees-button"
        >
          <font-awesome-icon icon="user-tag"/>
        </span>
        <span class="icon-btn"
              v-if="canEdit"
              @click="$emit('reminders')"
              v-b-tooltip.hover
              :title="$t('tasks.send_reminders')"
              data-qid="question-send_reminder-button"
        >
          <font-awesome-icon icon="reply-clock"/>
        </span>
        <span class="icon-btn"
              v-if="canEdit && objectives.length > 1"
              @click="$refs.orderModal.show(objectives)"
              v-b-tooltip.hover
              :title="$t('tasks.v2.objective.reorder')"
              data-qid="task-list-reorder-objectives"
        >
          <font-awesome-icon icon="list-ol"/>
        </span>
        <span class="icon-btn"
              @click="toggleAll"
              v-b-tooltip.hover
              :title="allCollapsed ? $t('tasks.v2.list.expand_all') : $t('tasks.v2.list.collapse_all')"
              data-qid="task-list-toggle-all"
        >
          <font-awesome-icon :icon="allCollapsed ? 'expand' : 'compress'"/>
        </span>
        <span class="icon-btn"
              v-if="canEdit"
              @click="$emit('add-task')"
              v-b-tooltip.hover
              :title="$t('tasks.v2.list.add_task')"
              data-qid="question-create-button"
        >
          <font-awesome-icon icon="plus"/>
        </span>
      </div>
    </div>

    <!-- Tasks grouped by control objective -->
    <table class="task-list__table">
      <thead>
        <tr>
          <th class="task-list__ref">{{ $t('tasks.v2.list.ref') }}</th>
          <th>{{ $t('tasks.v2.list.task') }}</th>
          <th>{{ $t('tasks.v2.list.stage') }}</th>
          <th>{{ $t('tasks.v2.list.response') }}</th>
          <th>{{ $t('tasks.v2.list.assignees') }}</th>
          <th>{{ $t('tasks.v2.list.sign_off') }}</th>
          <th class="text-right">{{ $t('tasks.v2.list.updated') }}</th>
          <th class="task-list__more"></th>
        </tr>
      </thead>

      <template v-for="group in groups">
        <tbody :key="'group-head-' + group.key" class="task-group">
          <tr class="task-group__row" @click="toggle(group.key)" :data-qid="'task-group-' + group.key">
            <td colspan="8">
              <div class="task-group__head">
                <font-awesome-icon :icon="collapsed[group.key] ? 'chevron-right' : 'chevron-down'" class="task-group__chevron"/>
                <span class="task-group__title">
                  <span class="task-group__name">{{ group.name }}</span>
                  <span v-if="group.objective && group.objective.description"
                        class="task-group__description"
                        :title="group.objective.description"
                  >{{ group.objective.description }}</span>
                </span>
                <!-- Light: finalized tasks; dark: signed off (never more than finalized) -->
                <span class="task-group__bar"
                      v-b-tooltip.hover
                      :title="$t('tasks.v2.list.progress_tooltip', { finalized: group.finalized, signed: group.signedOff, total: group.all })"
                >
                  <span class="task-group__bar-finalized" :style="{ width: group.finalizedPercent + '%' }"></span>
                  <span class="task-group__bar-signed" :style="{ width: group.percent + '%' }"></span>
                </span>
                <span class="task-group__count">
                  {{ $t('tasks.v2.list.progress_of', { finalized: group.finalized, signed: group.signedOff, total: group.all }) }}
                  <span v-if="group.rejected" class="text-red"> · {{ $tc('tasks.v2.list.rejected_count', group.rejected, { count: group.rejected }) }}</span>
                </span>
                <!-- Status of the control objective (no status yet is In progress); the comment goes with it -->
                <span v-if="group.objective && group.objective.id"
                      class="v2-tag task-group__status"
                      :class="objectiveTag(group.objective.status)"
                      v-b-tooltip.hover
                      :title="group.objective.comment || ''"
                      data-qid="task-group-status"
                >
                  {{ objectiveStatusText(group.objective.status) }}
                  <font-awesome-icon v-if="group.objective.comment" icon="comment" class="ml-1"/>
                </span>
                <span class="task-group__actions" v-if="canEdit && group.objective && group.objective.id" @click.stop>
                  <span class="icon-btn"
                        @click="$emit('edit-objective', group.objective)"
                        v-b-tooltip.hover
                        :title="$t('tasks.v2.list.edit_objective')"
                        data-qid="task-group-edit"
                  ><font-awesome-icon icon="pen"/></span>
                  <span class="icon-btn"
                        @click="$emit('delete-objective', group.objective)"
                        v-b-tooltip.hover
                        :title="$t('tasks.v2.list.delete_objective')"
                        data-qid="task-group-delete"
                  ><font-awesome-icon icon="trash-alt"/></span>
                </span>
              </div>
            </td>
          </tr>
        </tbody>

        <draggable v-if="!collapsed[group.key]"
                   :key="'group-rows-' + group.key"
                   :list="group.tasks"
                   tag="tbody"
                   handle=".task-list__drag"
                   :disabled="!canReorder"
                   ghost-class="ghost-row"
                   @end="saveOrder(group)"
        >
          <tr v-for="task in group.tasks"
              :key="'task-' + task.id"
              class="task-row"
              :class="{'task-row--rejected': task.qa_status === 'rejected'}"
              @click="openTask(task.id)"
              data-qid="task-row"
          >
            <td class="task-list__ref">
              <span v-if="canReorder" class="task-list__drag" @click.stop><font-awesome-icon icon="grip-vertical"/></span>
              {{ task.reference }}
            </td>
            <td class="task-row__title">
              <a href="#"
                 class="v2-task-title"
                 @click.prevent.stop="openTask(task.id)"
                 data-qid="task-row-open">{{ task.title }}</a>
              <div class="v2-signals" v-if="task.comment_unread_count || mediaCount(task)">
                <span v-if="task.comment_unread_count" class="v2-signal v2-signal--new">
                  <font-awesome-icon icon="comment" class="mr-1"/>{{ $tc('tasks.v2.new_messages', task.comment_unread_count, { count: task.comment_unread_count }) }}
                </span>
                <span v-if="mediaCount(task)" class="v2-signal">
                  <font-awesome-icon icon="paperclip" class="mr-1"/>{{ mediaCount(task) }}
                </span>
              </div>
            </td>
            <!-- Stage: the phase, and the documentation collection while it still matters (before finalizing) -->
            <td class="task-row__stage" data-qid="task-row-stage">
              <span class="v2-tag" :class="phaseOf(task).cls">{{ phaseOf(task).text }}</span>
              <span v-if="task.status !== 'completed' && task.data_collection_on"
                    class="task-doc"
                    :class="'task-doc--' + docOf(task).tone"
              >{{ docOf(task).text }}</span>
            </td>
            <td class="task-row__response" data-qid="task-row-response">
              <template v-if="task.response">
                <font-awesome-icon v-if="iconOf(task)"
                                   :icon="iconOf(task).icon"
                                   :style="iconOf(task).style"
                                   class="mr-1"
                />
                <span v-if="task.response_type === 'Datepicker'">{{ task.response | formatDate }}</span>
                <span v-else :title="task.response">{{ task.response }}</span>
              </template>
              <span v-else class="text-muted">–</span>
            </td>
            <td class="task-row__assignees">
              <span v-if="(task.users || []).length" class="task-row__avatars">
                <Avatar v-for="user in task.users.slice(0, 3)"
                        :key="'task-' + task.id + '-user-' + user.id"
                        :object="user"
                        size="xs"
                        v-b-tooltip.hover
                        :title="user.name"
                ></Avatar>
                <span v-if="task.users.length > 3" class="task-row__more">+{{ task.users.length - 3 }}</span>
              </span>
              <span v-else class="engagement-unassigned-pill" data-qid="task-unassigned-flag">{{ $t('projects.engagement.filter.unassigned') }}</span>
            </td>
            <td class="task-row__signoff">
              <span v-if="task.qa_status === 'rejected'" class="task-signoff task-signoff--red">
                <font-awesome-icon icon="times-circle" class="mr-1"/>{{ $t('system.rejected') }}
              </span>
              <span v-else class="task-signoff" :class="{'task-signoff--done': signedOff(task)}">
                <font-awesome-icon v-if="signedOff(task)" icon="check" class="mr-1"/>{{ (task.sign_off_count || 0) }} / {{ requiredSignOffs }}
              </span>
            </td>
            <td class="text-right task-row__updated">{{ relative(task.updated_at) }}</td>
            <td class="task-list__more" @click.stop>
              <b-dropdown v-if="canEdit"
                          size="sm"
                          variant="link"
                          right
                          no-caret
                          toggle-class="task-row__menu"
                          data-qid="task-row-menu"
              >
                <template #button-content><font-awesome-icon icon="ellipsis-h"/></template>
                <b-dropdown-item @click="openTask(task.id)">
                  <font-awesome-icon icon="external-link-alt" class="mr-2"/>{{ $t('tasks.open_button') }}
                </b-dropdown-item>
                <b-dropdown-item @click="$emit('assign', task)">
                  <font-awesome-icon icon="user-tag" class="mr-2"/>{{ $t('tasks.v2.list.assign') }}
                </b-dropdown-item>
                <b-dropdown-item @click="$emit('delete', task)" variant="danger">
                  <font-awesome-icon icon="trash-alt" class="mr-2"/>{{ $t('tasks.v2.list.delete_task') }}
                </b-dropdown-item>
              </b-dropdown>

              <!-- Every task has its own task host; on engagements it opens the task panel -->
              <span class="d-none">
                <AssessmentGlobalSingleTaskModal
                    :task="task"
                    :controlObjective="task.control_objective"
                    :ref="'task-modal-' + task.id"
                    :panel="true"
                    @reloadContent="$emit('reload')"
                    @navigate="navigateTask(task.id, $event)"
                ></AssessmentGlobalSingleTaskModal>
              </span>
            </td>
          </tr>
        </draggable>
      </template>

      <tbody v-if="!loading && !groups.length">
        <tr><td colspan="8" class="task-list__empty">{{ $t('system.no_results') }}</td></tr>
      </tbody>
      <tbody v-if="loading && !tasks.length">
        <tr><td colspan="8" class="task-list__empty"><font-awesome-icon icon="spinner" class="fa-spin"/></td></tr>
      </tbody>
    </table>

    <EngagementObjectiveOrderModal ref="orderModal" :project="project" @saved="loadObjectives"></EngagementObjectiveOrderModal>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import draggable from 'vuedraggable';
import Avatar from '@/components/Avatar.vue';
import AssessmentGlobalSingleTaskModal from '@/views/Tasks/AssessmentGlobalSingleTaskModal.vue';
import { responseIcon } from '@/views/Tasks/Assessment/responseIcon';
import EngagementObjectiveOrderModal from '@/views/Projects/Engagement/EngagementObjectiveOrderModal.vue';

// Engagement task list: all tasks grouped by control objective, filtered in the browser.
// Each row opens the task panel; previous / next in the panel follow the visible order.
export default {
  name: 'EngagementTaskList',
  props: {
    project: {
      type: Object,
      required: true
    },
    tasks: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    canManageAssignees: {
      type: Boolean,
      default: false
    }
  },
  components: {
    draggable,
    Avatar,
    AssessmentGlobalSingleTaskModal,
    EngagementObjectiveOrderModal
  },
  // The task panel asks the list for previous / next task and its position
  provide() {
    return { taskListNav: this };
  },
  data() {
    return {
      keyword: '',
      mine: false,
      filters: {
        phase: 'all',
        documentation: 'all',
        response: 'all',
        status: 'all'
      },
      collapsed: {},
      objectives: []
    };
  },
  computed: {
    getUser() {
      return this.$store.state.user;
    },
    requiredSignOffs() {
      return Math.max(1, parseInt(this.project.required_sign_offs, 10) || 0);
    },
    usesOe() {
      return this.project.project_type === 'type two';
    },
    canReorder() {
      return this.canEdit && !this.filtersActive && this.project.status !== 'closed' && this.project.status !== 'completed';
    },
    filterMenus() {
      const all = { name: this.$t('tasks.v2.list.all'), value: 'all' };
      let phases = [all, { name: this.$t('tasks.v2.stage_di'), value: 'di' }];
      if (this.usesOe) {
        phases.push({ name: this.$t('tasks.test_phase.operating_effectiveness'), value: 'oe' });
      }
      phases.push({ name: this.$t('tasks.v2.finalized'), value: 'finalized' });
      return [
        { key: 'phase', label: this.$t('tasks.v2.list.phase'), options: phases },
        { key: 'documentation', label: this.$t('tasks.v2.list.documentation'), options: [
          all,
          { name: this.$t('tasks.v2.list.client_not_started'), value: 'not_started' },
          { name: this.$t('system.documentation_in_progress'), value: 'in_progress' },
          { name: this.$t('tasks.v2.list.documentation_delivered'), value: 'delivered' }
        ] },
        { key: 'response', label: this.$t('tasks.v2.list.response'), options: this.responseOptions },
        { key: 'status', label: this.$t('tasks.v2.list.status'), options: [
          all,
          { name: this.$t('system.in_progress'), value: 'open' },
          { name: this.$t('tasks.v2.awaiting_qa'), value: 'awaiting_qa' },
          { name: this.$t('system.rejected'), value: 'rejected' },
          { name: this.$t('system.signed_off'), value: 'signed_off' },
          { name: this.$t('projects.engagement.filter.unassigned'), value: 'unassigned' }
        ] }
      ];
    },
    // The conclusion responses used on the tasks, and tasks without one
    responseOptions() {
      const responses = _.sortBy(_.uniq(_.compact(_.map(this.tasks, task => task.response_type !== 'Datepicker' ? task.response : null))));
      return [{ name: this.$t('tasks.v2.list.all'), value: 'all' }]
        .concat(_.map(responses, response => ({ name: response, value: response })))
        .concat([{ name: this.$t('tasks.v2.list.no_response'), value: 'none' }]);
    },
    allCollapsed() {
      return this.groups.length > 0 && _.every(this.groups, group => this.collapsed[group.key]);
    },
    filtersActive() {
      return !!this.keyword || this.mine || _.some(this.filters, value => value !== 'all');
    },
    visibleTasks() {
      return _.filter(this.tasks, task => this.passes(task));
    },
    groups() {
      const byObjective = _.groupBy(this.visibleTasks, task => task.control_objective_id || 0);
      const order = _.map(this.objectives, 'id');
      const keys = _.sortBy(_.keys(byObjective), key => {
        const index = order.indexOf(parseInt(key, 10));
        return index === -1 ? 9999 : index;
      });
      return _.map(keys, key => {
        const tasks = byObjective[key];
        const objective = _.find(this.objectives, { id: parseInt(key, 10) }) || (tasks[0] && tasks[0].control_objective) || null;
        const all = _.filter(this.tasks, task => String(task.control_objective_id || 0) === key);
        const signedOff = _.filter(all, task => this.signedOff(task)).length;
        const finalized = _.filter(all, task => task.status === 'completed').length;
        return {
          key: key,
          objective: objective,
          name: objective ? objective.name : this.$t('tasks.v2.list.no_objective'),
          tasks: tasks,
          all: all.length,
          signedOff: signedOff,
          finalized: finalized,
          finalizedPercent: all.length ? Math.round(finalized / all.length * 100) : 0,
          rejected: _.filter(all, { qa_status: 'rejected' }).length,
          percent: all.length ? Math.round(signedOff / all.length * 100) : 0
        };
      });
    },
    // Visible tasks in the order shown, for previous / next in the task panel
    orderedTasks() {
      return _.flatMap(this.groups, group => group.tasks);
    }
  },
  methods: {
    iconOf(task) {
      return responseIcon(task.response_type_options, task.response);
    },
    loadObjectives() {
      let self = this;
      piincHttp.get('project-control-objectives', { params: { project: this.project.id, per_page: 100 } }).then(function(response) {
        // The order set by the users; objectives without one keep the order they were added in
        self.objectives = _.sortBy(response.data.items || [], [item => item.order === null || item.order === undefined ? Number.MAX_SAFE_INTEGER : item.order, 'id']);
      }, function() {});
    },
    optionLabel(filter, value) {
      const option = _.find(filter.options, { value: value });
      return option ? option.name : '';
    },
    resetFilters() {
      this.keyword = '';
      this.mine = false;
      this.filters = { phase: 'all', documentation: 'all', response: 'all', status: 'all' };
    },
    // Collapse every control objective, or expand them all again
    toggleAll() {
      const collapse = !this.allCollapsed;
      this.collapsed = _.fromPairs(_.map(this.groups, group => [group.key, collapse]));
    },
    toggle(key) {
      this.$set(this.collapsed, key, !this.collapsed[key]);
    },
    signedOff(task) {
      return task.status === 'completed' && task.qa_status !== 'rejected' && (task.sign_off_count || 0) >= this.requiredSignOffs;
    },
    mediaCount(task) {
      return (task.media ? task.media.length : 0) + (task.linked_media ? task.linked_media.length : 0);
    },
    phaseKey(task) {
      if (task.status === 'completed') {
        return 'finalized';
      }
      return this.usesOe && task.test_phase === 'operating_effectiveness' ? 'oe' : 'di';
    },
    phaseOf(task) {
      const phases = {
        finalized: { cls: 'v2-tag--teal', text: this.$t('tasks.v2.finalized') },
        oe: { cls: 'v2-tag--blue', text: this.$t('tasks.v2.stage_oe_short') },
        di: { cls: 'v2-tag--mint', text: this.$t('tasks.v2.stage_di_short') }
      };
      return phases[this.phaseKey(task)];
    },
    docKey(task) {
      if (!task.data_collection_on) {
        return 'none';
      }
      if (task.data_status === 'completed') {
        return 'delivered';
      }
      return task.data_remarks || this.mediaCount(task) ? 'in_progress' : 'not_started';
    },
    docOf(task) {
      const docs = {
        none: { tone: 'none', text: this.$t('tasks.v2.list.not_requested') },
        delivered: { tone: 'violet', text: this.$t('tasks.v2.list.documentation_delivered') },
        in_progress: { tone: 'amber', text: this.$t('tasks.v2.waiting_on_client') },
        not_started: { tone: 'grey', text: this.$t('tasks.v2.list.client_not_started') }
      };
      return docs[this.docKey(task)];
    },
    statusKey(task) {
      if (task.qa_status === 'rejected') {
        return 'rejected';
      }
      if (task.status !== 'completed') {
        return 'open';
      }
      return this.signedOff(task) ? 'signed_off' : 'awaiting_qa';
    },
    passes(task) {
      if (this.keyword) {
        const keyword = this.keyword.toLowerCase();
        if (!((task.full_title || '') + ' ' + (task.title || '')).toLowerCase().includes(keyword)) {
          return false;
        }
      }
      if (this.mine && !(this.getUser && _.some(task.users || [], { id: this.getUser.id }))) {
        return false;
      }
      if (this.filters.phase !== 'all' && this.phaseKey(task) !== this.filters.phase) {
        return false;
      }
      if (this.filters.documentation !== 'all' && this.docKey(task) !== this.filters.documentation) {
        return false;
      }
      if (this.filters.response !== 'all' && (this.filters.response === 'none' ? !!task.response : task.response !== this.filters.response)) {
        return false;
      }
      if (this.filters.status === 'unassigned') {
        return !(task.users || []).length;
      }
      return this.filters.status === 'all' || this.statusKey(task) === this.filters.status;
    },
    objectiveStatusText(status) {
      return this.$t('testing.status.' + this.$options.filters.slugify(status || 'In progress'));
    },
    objectiveTag(status) {
      return { Passed: 'v2-tag--teal', Failed: 'v2-tag--red' }[status] || 'v2-tag--amber';
    },
    relative(value) {
      if (!value) {
        return '';
      }
      const moment = require('moment');
      const days = moment().startOf('day').diff(moment(value).startOf('day'), 'days');
      if (days === 0) {
        return this.$t('projects.schedule.today');
      }
      if (days === 1) {
        return this.$t('tasks.v2.list.yesterday');
      }
      return days < 7 ? this.$tc('projects.schedule.days_ago', days, { count: days }) : this.$options.filters.formatDate(value, 'DD MMM', true);
    },
    // Dragging reorders the tasks of one control objective
    saveOrder(group) {
      let self = this;
      piincHttp.post('projects/' + this.project.slug + '/reorder', {
        project_id: this.project.id,
        tasks: _.map(group.tasks, (task, index) => ({ id: task.id, order: index + 1 }))
      }).then(function() {
        self.$toastr('success', self.$t('tasks.v2.list.order_saved'));
        self.$emit('reload');
      }, function() {});
    },
    hostOf(taskId) {
      const host = this.$refs['task-modal-' + taskId];
      return Array.isArray(host) ? host[0] : host;
    },
    openTask(taskId) {
      const instance = this.hostOf(taskId);
      if (instance) {
        instance.showTaskModal(instance.task);
      }
    },
    navigateTask(taskId, direction) {
      const index = _.findIndex(this.orderedTasks, task => task.id === taskId);
      const next = this.orderedTasks[index + direction];
      if (!next) {
        return;
      }
      const current = this.hostOf(taskId);
      if (current) {
        current.hideTaskModal();
      }
      this.$nextTick(() => this.openTask(next.id));
    },
    taskPosition(taskId) {
      return { index: _.findIndex(this.orderedTasks, task => task.id === taskId) + 1, total: this.orderedTasks.length };
    }
  },
  watch: {
    'project.id': {
      handler(value) {
        if (value) {
          this.loadObjectives();
        }
      },
      immediate: true
    }
  }
};
</script>
